import { Injectable } from '@angular/core';
import { Ability } from '@casl/ability';
import { map } from 'rxjs/operators';
import { AppHttpService } from './app-http.service';
import { WorkflowAction, WorkflowLineage } from '../models';

@Injectable({ providedIn: 'root' })
export class WorkflowService {

  constructor(
    private appHttpService: AppHttpService,
    private ability: Ability,
  ) {}

  /**
   * Whether this session may run workflow transitions. Mirrors the API's
   * `execute Workflow` gate on POST /v1/workflow/execute (revoked for
   * org web read-only sessions), so buttons only render when the click
   * would succeed.
   */
  canExecute(): boolean {
    return this.ability.can('execute', 'Workflow');
  }

  /**
   * Returns the entity with its `next-actions` emptied when the session
   * cannot execute workflows. Use at the point an entity is stored on a
   * component so every downstream read of `next-actions` is gated at once.
   */
  gateNextActions<T extends Record<string, unknown>>(entity: T): T {
    if (!entity || this.canExecute() || !entity['next-actions']) {
      return entity;
    }
    return { ...entity, 'next-actions': [] } as T;
  }

  /**
   * Fetches next actions in given workflow
   * @param wf string
   * @param id subject.id
   * @param projectId Project.id
   */
  getNextActions(wf: string, id: string, projectId: string) {
    const qp = { wf, id, project_id: projectId };
    return this.appHttpService.getService('/v1/workflow/next-state', qp).pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data.map(o => new WorkflowAction(o));
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Execute workflow-actions via REST
   * @param ex ex-id
   * @param wf workflow
   * @param state state
   * @param projectId Project.id
   * @param input input-params
   */
  executeState(ex: string, wf: string, state: string, projectId: string, input: any) {
    const qp = { wf, ex, state, project_id: projectId, include: '' };
    const payload = { input };
    return this.appHttpService.postService('/v1/workflow/execute', payload, qp);
  }

  /**
   * Fetches workflow lineage
   * @param ex execution id
   * @param pagination pagination obj
   * @param qp query-params
   */
  fetchLineage(ex: any, pagination, qp: any = {}) {
    // Create a mutable copy of the query parameters object
    const queryParams = { ...qp };

    queryParams.start = pagination.pageIndex * pagination.pageSize;
    queryParams.total = pagination.pageSize;
    queryParams.ex = ex;

    return this.appHttpService.getService('/v1/workflow/lineage', queryParams).pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          const result = resp.data.map(r => new WorkflowLineage(r));
          const meta = Object.assign(resp.meta || {}, { type: resp.type });
          return { result, meta };
        }
        throw new Error('Error parsing response');
      })
    );
  }

}
